import { create } from "zustand"
import { persist } from "zustand/middleware"

export type TagCategory = "artist" | "character" | "copyright" | "series" | "general" | "meta" | "va" | "sound_designer"

export interface TagCategorization {
  tag: string // Normalized (lowercase) tag
  category: TagCategory
  displayName?: string // Optional display name (preserves original casing)
  categorizedBy?: string // User ID who categorized it
  categorizedAt: number
  aliases?: string[] // Alternative names for the same tag
}

interface TagTaxonomyState {
  categorizations: Map<string, TagCategorization>

  // Actions
  categorizeTag: (tag: string, category: TagCategory, userId?: string, displayName?: string) => void
  getCategorization: (tag: string) => TagCategorization | undefined
  getCategory: (tag: string) => TagCategory | undefined
  bulkCategorize: (tags: Array<{ tag: string; category: TagCategory }>, userId?: string) => void
  removeCategorization: (tag: string) => void
  getAllByCategory: (category: TagCategory) => TagCategorization[]
  addAlias: (tag: string, alias: string) => void
  searchTags: (query: string) => TagCategorization[]
  exportCategorizations: () => TagCategorization[]
  importCategorizations: (categorizations: TagCategorization[]) => void
}

// Normalize tag for case-insensitive matching
const normalizeTag = (tag: string): string => {
  return tag.toLowerCase().trim()
}

export const useTagTaxonomyStore = create<TagTaxonomyState>()(
  persist(
    (set, get) => ({
      categorizations: new Map(),

      categorizeTag: (tag, category, userId, displayName) => {
        const normalized = normalizeTag(tag)
        set((state) => {
          const newCategorizations = new Map(state.categorizations)
          newCategorizations.set(normalized, {
            tag: normalized,
            category,
            displayName: displayName || tag,
            categorizedBy: userId,
            categorizedAt: Date.now(),
          })
          return { categorizations: newCategorizations }
        })
      },

      getCategorization: (tag) => {
        const normalized = normalizeTag(tag)
        return get().categorizations.get(normalized)
      },

      getCategory: (tag) => {
        const normalized = normalizeTag(tag)
        return get().categorizations.get(normalized)?.category
      },

      bulkCategorize: (tags, userId) => {
        set((state) => {
          const newCategorizations = new Map(state.categorizations)
          tags.forEach(({ tag, category }) => {
            const normalized = normalizeTag(tag)
            newCategorizations.set(normalized, {
              tag: normalized,
              category,
              displayName: tag,
              categorizedBy: userId,
              categorizedAt: Date.now(),
            })
          })
          return { categorizations: newCategorizations }
        })
      },

      removeCategorization: (tag) => {
        const normalized = normalizeTag(tag)
        set((state) => {
          const newCategorizations = new Map(state.categorizations)
          newCategorizations.delete(normalized)
          return { categorizations: newCategorizations }
        })
      },

      getAllByCategory: (category) => {
        return Array.from(get().categorizations.values()).filter((c) => c.category === category)
      },

      addAlias: (tag, alias) => {
        const normalized = normalizeTag(tag)
        const normalizedAlias = normalizeTag(alias)
        set((state) => {
          const categorization = state.categorizations.get(normalized)
          if (!categorization) return state

          const newCategorizations = new Map(state.categorizations)
          const aliases = categorization.aliases || []
          if (!aliases.includes(normalizedAlias)) {
            newCategorizations.set(normalized, {
              ...categorization,
              aliases: [...aliases, normalizedAlias],
            })
          }
          return { categorizations: newCategorizations }
        })
      },

      searchTags: (query) => {
        const normalized = normalizeTag(query)
        return Array.from(get().categorizations.values()).filter(
          (c) =>
            c.tag.includes(normalized) ||
            c.displayName?.toLowerCase().includes(normalized) ||
            c.aliases?.some((a) => a.includes(normalized)),
        )
      },

      exportCategorizations: () => {
        return Array.from(get().categorizations.values())
      },

      importCategorizations: (categorizations) => {
        set((state) => {
          const newCategorizations = new Map(state.categorizations)
          categorizations.forEach((c) => {
            const normalized = normalizeTag(c.tag)
            newCategorizations.set(normalized, c)
          })
          return { categorizations: newCategorizations }
        })
      },
    }),
    {
      name: "tag-taxonomy-storage",
      // Custom serialization for Map
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name)
          if (!str) return null
          const { state } = JSON.parse(str)
          return {
            state: {
              ...state,
              categorizations: new Map(state.categorizations || []),
            },
          }
        },
        setItem: (name, value) => {
          const { state } = value
          localStorage.setItem(
            name,
            JSON.stringify({
              state: {
                ...state,
                categorizations: Array.from(state.categorizations.entries()),
              },
            }),
          )
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
)
